import { registerIntegration } from "@p0security/shared/integrations/registry";
import {
  type AnyFilterSchema,
  Registration,
} from "@p0security/shared/integrations/types";

import { MiroAiAccesses } from "./accesses.ts";
import { MiroAiComponents } from "./components.ts";
import { MiroAiIconUrl } from "./icon.ts";

// Rule filter values compile to regular expressions, so a rule can say, e.g.,
// that `viewer` on a board named `^\[Public\]` is preapproved. `team` matches
// the team's item id. A board's name is its owner's to change, so rules that
// must hold should key on its id.
const MiroAiFilters = {
  team: { keys: ["id"] },
  board: { keys: ["id", "name"] },
  role: { keys: ["name"] },
} as const satisfies AnyFilterSchema;
export type MiroAiFilters = typeof MiroAiFilters;

// Keyed by component, so a new component doesn't compile until it has one.
// The new item's form labels an item's id with its component's kind, then
// this: "connector ID", "team identifier".
const IDENTIFIERS: Record<keyof typeof MiroAiComponents, string> = {
  connector: "ID",
  team: "identifier",
};
// `Registration` hands `identifier` any string, not just a component key.
const identifiers: Partial<Record<string, string>> = IDENTIFIERS;

export const MiroAiRegistration = Registration({
  accesses: MiroAiAccesses,
  category: "resource",
  components: MiroAiComponents,
  // A path here becomes a link in the product, so there's none until the
  // docs page exists (INT-1116).
  docs: {},
  filters: MiroAiFilters,
  identifier: (component) => identifiers[component] ?? component,
  // Each component is named for what one of its items is.
  itemKind: (component) => component,
  label: "Miro (AI)",
  logoUrl: MiroAiIconUrl,
  maturity: "beta",
  // The agent's request routes by the CLI's resource-type positional, so
  // `p0 request miro-ai board …` only works if this is requestable.
  requestType: "requestable",
  // A connector alone grants nothing, so Miro (AI) counts as installed once
  // a team is.
  useMapping: [{ component: "team", use: "iam_write" }],
});

registerIntegration("miro-ai", MiroAiRegistration);
